import PropTypes from "prop-types";
import React from "react";

import { getBootstrapData } from "@swa-ui/bootstrap";
import { DialogHeading, DialogSubheading, Link, PageGrid } from "@swa-ui/core";
import { CheckboxFormField } from "@swa-ui/form";
import i18n from "@swa-ui/locale";
import { NewUsernameFormField, SecurityQuestionsFormField } from "@swa-ui/loyalty";
import { EmailFormField, PhoneFormFields } from "@swa-ui/personal-info";
import { classNames } from "@swa-ui/string";

import styles from "./MissingInformationForm.module.scss";

/**
 * MissingInformationForm is a form that allows the user to provide missing information.
 */
export const MissingInformationForm = (props) => {
  const { loading, missingInformation, onUsernameAvailabilityStatusChange } = props;
  const { FAQ__RAPID_REWARDS__RULES_AND_REGULATIONS } = getBootstrapData("urls");

  return (
    <span className={styles.container}>
      <DialogHeading>{i18n("MissingInformationForm__HEADING")}</DialogHeading>
      <DialogSubheading>
        <p>
          {i18n("MissingInformationForm__SUBHEADING", {
            formattedMissingInformation: getFormattedMissingInformation(),
          })}
        </p>
        <p>{i18n("MissingInformationForm__ALL_REQUIRED")}</p>
      </DialogSubheading>

      <PageGrid className={styles.grid}>
        {isMissing("EMAIL") && <EmailFormField className={styles.fullWidth} disabled={loading} />}
        {isMissing("PHONE") && <PhoneFormFields {...getPhoneFormFieldsProps()} />}
        {isMissing("USERNAME") && <NewUsernameFormField {...getNewUsernameFormFieldProps()} />}
        {isMissing("SECURITY_CHALLENGE") && (
          <SecurityQuestionsFormField {...getSecurityQuestionsFormFieldProps()} />
        )}
        <CheckboxFormField {...getTermsAgreeProps()} />
      </PageGrid>
    </span>
  );

  function getNewUsernameFormFieldProps() {
    return {
      className: styles.fullWidth,
      componentProps: {
        className: styles.usernameInput,
      },
      onUsernameAvailabilityStatusChange,
    };
  }

  function getPhoneFormFieldsProps() {
    return {
      className: styles.fullWidth,
      disabled: loading,
      phoneType: "MOBILE",
      showPhoneType: true,
    };
  }

  function getSecurityQuestionsFormFieldProps() {
    return {
      answer1ClassName: styles.fullWidth,
      answer2ClassName: styles.fullWidth,
      disabled: loading,
      question1ClassName: styles.fullWidth,
      question2ClassName: styles.fullWidth,
    };
  }

  function getTermsAgreeProps() {
    return {
      className: classNames(styles.fullWidth, styles.termsAgree),
      componentProps: {
        label: (
          <span>
            {i18n("MissingInformationForm__TERMS_AGREE")}
            <Link {...getTermsLinkProps()}>
              {i18n("MissingInformationForm__RULES_AND_REGULATIONS")}
            </Link>
          </span>
        ),
      },
      disabled: loading,
      name: "termsAgree",
    };
  }

  function getTermsLinkProps() {
    return {
      external: true,
      href: FAQ__RAPID_REWARDS__RULES_AND_REGULATIONS,
      newWindow: true,
      onClick: (event) => event.stopPropagation(),
      size: "fontSize14",
    };
  }

  function isMissing(type) {
    return missingInformation?.some?.((value) => value === type);
  }

  function getFormattedMissingInformation() {
    return (missingInformation ?? [])
      .map((info) => i18n(`MissingInformationForm__${info}`))
      .join(", ")
      .replace(/, ([^,]*)$/, ` ${i18n("MissingInformationForm__AND")} $1`);
  }
};

MissingInformationForm.propTypes = {
  /** Loading state */
  loading: PropTypes.bool,

  /** Array of missing information */
  missingInformation: PropTypes.array,

  /** Callback for username availability status change */
  onUsernameAvailabilityStatusChange: PropTypes.func.isRequired,
};
